import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('ad_master_token'));
    const [loading, setLoading] = useState(true);
    const [activeCompany, setActiveCompany] = useState(null);

    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            fetchUser();
        } else {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when these inputs change; the loader is recreated each render and reads current state
    }, [token]);

    const fetchUser = async () => {
        try {
            const response = await axios.get(`${API_URL}/auth/me`);
            setUser(response.data);
            if (response.data.active_company_id && response.data.role === 'superadmin') {
                // Fetch company name for active company
                try {
                    const companyRes = await axios.get(`${API_URL}/superadmin/company/${response.data.active_company_id}`);
                    setActiveCompany({ id: response.data.active_company_id, name: companyRes.data.name });
                } catch (e) {
                    setActiveCompany({ id: response.data.active_company_id });
                }
            }
        } catch (error) {
            console.error('Auth error:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        const response = await axios.post(`${API_URL}/auth/login`, { email, password });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('ad_master_token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(userData);
        if (userData.active_company_id) {
            setActiveCompany({ id: userData.active_company_id });
        }
        return userData;
    };

    const register = async (email, password, name, company_name) => {
        const response = await axios.post(`${API_URL}/auth/register`, {
            email,
            password,
            name,
            company_name
        });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('ad_master_token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('ad_master_token');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
        setActiveCompany(null);
    };

    const switchCompany = async (companyId, companyName) => {
        const response = await axios.post(`${API_URL}/superadmin/switch-company`, { company_id: companyId });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('ad_master_token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(userData);
        setActiveCompany({ id: companyId, name: companyName });
        return userData;
    };

    const isSuperAdmin = () => user?.role === 'superadmin';
    const isAdmin = () => user?.role === 'admin' || user?.role === 'superadmin';
    const isManager = () => user?.role === 'manager' || user?.role === 'admin' || user?.role === 'superadmin';

    return (
        <AuthContext.Provider value={{
            user,
            token,
            loading,
            activeCompany,
            login,
            register,
            logout,
            switchCompany,
            isSuperAdmin,
            isAdmin,
            isManager
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
